<template>
  <Drawer
    :aria-label="`Deliveries for ${subscription.trigger.label}`"
    no-footer
    data-cy="zapier-deliveries-drawer"
    @hide-drawer="emit('close')"
  >
    <template #title>
      <div class="pr-10">
        <h2 class="mb-1 text-base font-semibold text-gray-900">
          {{ subscription.trigger.label }}
        </h2>
        <p class="m-0 text-sm text-gray-600">
          {{ destinationLabel(subscription) }} ·
          <code class="text-xs">{{
            subscription.destination.fingerprint
          }}</code>
        </p>
        <p
          v-if="subscription.archived_at"
          class="m-0 mt-1 text-xs text-gray-500"
          data-cy="zapier-drawer-stopped"
        >
          {{ stoppedReasonCopy(subscription.archived_reason) }} · Stopped
          {{ formatDateWithTimezone(subscription.archived_at) }}
        </p>
      </div>
    </template>

    <div class="px-4 pb-4">
      <div
        class="mb-3 flex flex-wrap gap-2"
        role="group"
        aria-label="Filter by status"
      >
        <button
          v-for="option in filterOptions"
          :key="option.value ?? 'all'"
          type="button"
          class="rounded-full border px-3 py-1 text-xs font-medium"
          :class="
            status === option.value
              ? 'border-blue-600 bg-blue-50 text-blue-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
          "
          :aria-pressed="status === option.value"
          :data-cy="`zapier-delivery-filter:${option.value ?? 'all'}`"
          @click="setStatus(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div
        v-if="loading && !deliveries.length"
        class="flex justify-center py-6"
      >
        <Spinner size="small" />
      </div>

      <Alert v-else-if="loadFailed" type="error" class="mb-3">
        Deliveries could not be loaded.
        <button
          type="button"
          class="link ml-1"
          data-cy="zapier-deliveries-retry"
          @click="load(currentPage)"
        >
          Try again
        </button>
      </Alert>

      <p
        v-else-if="!deliveries.length"
        class="py-6 text-center text-sm text-gray-500"
        data-cy="zapier-deliveries-empty"
      >
        {{ emptyText }}
      </p>

      <table
        v-else
        class="w-full text-left text-sm text-gray-700"
        :class="{ 'opacity-60': loading }"
        data-cy="zapier-deliveries-table"
      >
        <thead class="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th class="px-2 py-2 font-semibold">Time</th>
            <th class="px-2 py-2 font-semibold">Status</th>
            <th class="px-2 py-2 font-semibold">HTTP</th>
            <th class="px-2 py-2 font-semibold">Outcome</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="delivery in deliveries" :key="delivery.id">
            <tr
              class="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
              :aria-expanded="expandedId === delivery.id"
              tabindex="0"
              :data-cy="`zapier-delivery-row:${delivery.id}`"
              @click="toggleRow(delivery.id)"
              @keydown.enter.prevent="toggleRow(delivery.id)"
            >
              <td class="whitespace-nowrap px-2 py-2">
                <span :title="formatDateTimeWithTimezone(delivery.created_at)">
                  {{ fromNow(delivery.created_at, true) }}
                </span>
              </td>
              <td class="px-2 py-2">
                <span
                  class="whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                  :class="statusStyle(delivery.status).pill"
                  data-cy="zapier-delivery-status"
                  >{{ statusStyle(delivery.status).label }}</span
                >
              </td>
              <td class="px-2 py-2">{{ delivery.response_code ?? "—" }}</td>
              <td class="px-2 py-2">{{ delivery.outcome.title }}</td>
            </tr>
            <tr
              v-if="expandedId === delivery.id"
              class="border-b border-gray-100 bg-gray-50"
            >
              <td colspan="4" class="space-y-3 px-2 py-3">
                <p
                  v-if="delivery.outcome.hint"
                  class="m-0 text-sm text-gray-700"
                  data-cy="zapier-delivery-hint"
                >
                  {{ delivery.outcome.hint }}
                </p>
                <p class="m-0 text-xs text-gray-500">
                  {{ formatDateTimeWithTimezone(delivery.created_at) }}
                </p>
                <button
                  v-if="!details[delivery.id]"
                  type="button"
                  class="link text-sm"
                  :disabled="loadingDetailId === delivery.id"
                  data-cy="zapier-delivery-view-payload"
                  @click.stop="loadDetail(delivery.id)"
                >
                  {{
                    loadingDetailId === delivery.id
                      ? "Loading…"
                      : "View payload"
                  }}
                </button>
                <template v-else>
                  <div>
                    <p
                      class="mb-1 text-xs font-semibold uppercase text-gray-500"
                    >
                      Response
                    </p>
                    <pre
                      v-if="details[delivery.id].response_excerpt"
                      class="m-0 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded border border-gray-200 bg-white p-2 font-mono text-xs"
                      data-cy="zapier-delivery-response"
                      >{{ details[delivery.id].response_excerpt }}</pre>
                    <p v-else class="m-0 text-xs text-gray-500">
                      No response body was recorded.
                    </p>
                  </div>
                  <div
                    v-if="details[delivery.id].attempts?.length"
                    data-cy="zapier-delivery-attempts"
                  >
                    <p
                      class="mb-1 text-xs font-semibold uppercase text-gray-500"
                    >
                      Attempts
                    </p>
                    <ol
                      class="m-0 list-none space-y-1 p-0 text-xs text-gray-700"
                    >
                      <li
                        v-for="attempt in details[delivery.id].attempts"
                        :key="attempt.number"
                        :data-cy="`zapier-delivery-attempt:${attempt.number}`"
                      >
                        #{{ attempt.number }} ·
                        {{ formatDateTimeWithTimezone(attempt.started_at) }} ·
                        HTTP {{ attempt.response_code ?? "—" }} ·
                        {{ attempt.outcome.title }}
                      </li>
                    </ol>
                  </div>
                  <JsonViewer
                    :value="details[delivery.id].payload"
                    :title="payloadTitle(details[delivery.id])"
                    empty-text="No payload was recorded."
                  />
                </template>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <PaginationControls
        v-if="pagination && pagination.total_pages > 1"
        class="mt-3"
        :pager="pager"
        no-page-length
      />
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import {
  formatDateTimeWithTimezone,
  formatDateWithTimezone
} from "@/lib/helpers";
import type Pagination from "@/lib/pagination";

import JsonViewer from "@/components/JsonViewer.vue";
import PaginationControls from "@/components/PaginationControls.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { fromNow } from "@/mixins";
import type {
  ZapierDeliveriesResponse,
  ZapierDelivery,
  ZapierDeliveryDetail,
  ZapierDeliveryPagination,
  ZapierDeliveryStatus,
  ZapierSubscription
} from "@/vuex/modules/zapierSubscriptions";
import {
  DELIVERY_STATUSES,
  destinationLabel,
  statusStyle,
  stoppedReasonCopy
} from "./zapierPanel";

const props = defineProps<{ subscription: ZapierSubscription }>();
const emit = defineEmits<{ close: [] }>();

const store = useStore();

const PER_PAGE = 25;

const status = ref<ZapierDeliveryStatus | null>(null);
const deliveries = ref<ZapierDelivery[]>([]);
const pagination = ref<ZapierDeliveryPagination | null>(null);
const loading = ref(false);
const loadFailed = ref(false);
// Legacy deliveries have integer ids, engine deliveries whmsg_ public ids.
type DeliveryId = ZapierDelivery["id"];

const expandedId = ref<DeliveryId | null>(null);
const loadingDetailId = ref<DeliveryId | null>(null);
const details = reactive<Record<DeliveryId, ZapierDeliveryDetail>>({});

const filterOptions = [
  { value: null, label: "All" },
  ...DELIVERY_STATUSES.map((value) => ({
    value,
    label: statusStyle(value).label
  }))
];

const currentPage = computed(() => pagination.value?.page ?? 1);

const emptyText = computed(() =>
  status.value
    ? `No ${statusStyle(status.value).label.toLowerCase()} deliveries in the last 30 days.`
    : "No deliveries in the last 30 days."
);

// PaginationControls only reads meta.pagination and calls goToPage. The
// deliveries endpoint takes a plain `status` param, which lib/pagination
// would send as filter[status], so this stands in for that class.
const pager = computed(
  () =>
    ({
      meta: { pagination: pagination.value },
      goToPage: async (page: number | string) => {
        await load(Number(page));
        return null;
      }
    }) as unknown as Pagination
);

// A filter or page change can overtake a slower earlier request; only the
// latest one may write.
let requestSeq = 0;

async function load(page = 1) {
  const seq = ++requestSeq;
  loading.value = true;
  try {
    const response: ZapierDeliveriesResponse = await store.dispatch(
      "fetchZapierSubscriptionDeliveries",
      {
        subscriptionId: props.subscription.id,
        status: status.value,
        page,
        perPage: PER_PAGE
      }
    );
    if (seq !== requestSeq) return;
    deliveries.value = response.data;
    pagination.value = response.meta.pagination;
    loadFailed.value = false;
    expandedId.value = null;
  } catch {
    if (seq !== requestSeq) return;
    deliveries.value = [];
    loadFailed.value = true;
    window.notify.error("Could not load Zapier deliveries.");
  } finally {
    if (seq === requestSeq) loading.value = false;
  }
}

function setStatus(value: ZapierDeliveryStatus | null) {
  if (status.value === value) return;
  status.value = value;
  load(1);
}

// An engine Zap's payload is the webhook event envelope.
function payloadTitle(detail: ZapierDeliveryDetail): string {
  return detail.payload_style === "envelope" ? "Event sent" : "Payload sent";
}

function toggleRow(id: DeliveryId) {
  expandedId.value = expandedId.value === id ? null : id;
}

async function loadDetail(id: DeliveryId) {
  loadingDetailId.value = id;
  try {
    details[id] = await store.dispatch("fetchZapierDelivery", id);
  } catch {
    window.notify.error("Could not load that delivery.");
  } finally {
    loadingDetailId.value = null;
  }
}

onMounted(() => load(1));
</script>
